package com.dynamicruntime.webapp

import com.dynamicruntime.common.fileref.FREF
import com.dynamicruntime.common.user.UADEP
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.code
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useRef
import react.useState
import web.cssom.ClassName

private val fileRefsScope = MainScope()

external interface FileRefsSectionProps : Props {
    var clientId: String
    /** When this client is a sandbox, the client it is the sandbox of: what is listed is that client's latest. */
    var sandboxOf: String?
    /** Whether the client has a sandbox, so a change made here is a draft. */
    var hasSandbox: Boolean
    /** Whether the client takes nothing stored on this node: its files are listed, and none is changed here. */
    var staticHere: Boolean
    /** What a change calls once it has taken effect: the page re-reads. */
    var onChanged: () -> Unit
}

/**
 * A client's file refs on its detail page (issue #1176): each named image its configuration holds -- a preview,
 * what the file is, where it is and the address it is served at -- with **Replace**, **Reword** and **Remove**,
 * and under the list the way to add one: an upload, or where the deployment has opened a root to clients (issue
 * #1164), a file it keeps for the client there. Read from `/clientAdmin/client/files` and `…/files/takes` on mount
 * and again on every refresh generation.
 *
 * **Unlike a web asset, a file ref is configuration**, so a change is live at once only for a client that runs its
 * latest: for one with a sandbox it is a draft the sandbox shows, and the section says which above the list
 * ([fileRefsIntro]) and again after each change. What is listed is then the sandbox's, each ref marked when the
 * client's own people are not served it yet -- and after it the refs they are served that a draft removed (issue
 * #1188), each marked so, previewed from what is served, and offered only **Put back**. Every change is sent with how the page said it would take effect
 * ([expectedMode]), and the backend refuses one the page called a draft that would be live: the page words a removal
 * from what it last read of the client, and a sandbox can go in between. Refused so ([saveConflict]), the page is
 * read again, and says how a change now takes effect.
 *
 * **Finding one among many** (issue #1190): a Find box over id and description and a toggle per mark the list
 * carries ([fileRefMatches]), the rows in a stated order -- what needs attention first, then by id
 * ([fileRefOrdered]) -- and no more than [fileRefFirstShown] until "Show all", so the rest of the client's page stays
 * in reach ([fileRefPage]). An address has a [CopyButton] beside it, and the add form's fields their names.
 *
 * Picking a file *is* the upload, as for a web asset; a removal is asked about first. Each answers the ref as it
 * now is, which is drawn at once, and bumps the refresh generation through [FileRefsSectionProps.onChanged].
 *
 * What it decides and what it says are `FileRefs.kt`'s, pure and tested; what is here is the state and the drawing.
 */
val FileRefsSection = FC<FileRefsSectionProps> { props ->
    var refs by useState<List<FileRefView>?>(null)
    var takes by useState<FileRefTakes?>(null)
    // Whether what a ref takes was asked for and could not be had: nothing is added from here then, and it is said.
    var takesFailed by useState(false)
    var loadError by useState<DisplayError?>(null)
    // What is running, which ref's removal is being asked about, and where the last failure was (issue #1388): a ref's
    // id, or [FACT.adding] for the form under the list.
    val rows = useRowActions()
    val status = rows.status
    var note by useState<String?>(null)
    // The one row editor open, if any: which ref, and which editor -- rewording it (`FACT.describe`) or pointing it
    // at a file the deployment keeps (`FACT.local`, issue #1189). One at a time in the whole section, as rewording
    // always was, so opening one closes any other.
    var editing by useState<Pair<String, String>?>(null)
    // What has been typed into the rewording.
    var describeText by useState("")
    // Where and what, for the file the ref is being pointed at.
    var repointRoot by useState<String?>(null)
    var repointPath by useState("")
    // The form that adds one.
    var newId by useState("")
    var newDescription by useState("")
    var newRoot by useState<String?>(null)
    var newPath by useState("")
    // What the list is narrowed to, and whether all of it is drawn (issue #1190).
    var findText by useState("")
    var marks by useState<Set<String>>(emptySet())
    var showAll by useState(false)
    // The refs this page added or changed: drawn whatever the filter and the cap, so a result is seen where it was made.
    var kept by useState<Set<String>>(emptySet())
    val latest = useRef(0)
    val lastClient = useRef<String>(null)
    val generation = useRefreshGeneration()

    useEffect(props.clientId, generation) {
        val token = (latest.current ?: 0) + 1
        latest.current = token
        // A new client: nothing of the previous one's refs, failures or half-filled form carries over.
        if (lastClient.current != props.clientId) {
            lastClient.current = props.clientId
            refs = null
            takes = null
            takesFailed = false
            loadError = null
            rows.clear()
            note = null
            editing = null
            describeText = ""
            repointRoot = null
            repointPath = ""
            newId = ""
            newDescription = ""
            newRoot = null
            newPath = ""
            findText = ""
            marks = emptySet()
            showAll = false
            kept = emptySet()
        }
        fileRefsScope.launch {
            try {
                val loaded = ClientsApi.fileRefs(props.clientId)
                if (latest.current == token) {
                    refs = loaded
                    loadError = null
                }
            } catch (e: Throwable) {
                if (latest.current == token) loadError = userFacingError(e)
            }
        }
        // By itself: what a ref takes is not needed to list the refs, and a node that cannot say it -- one a release
        // behind, mid-deploy -- still lists them. Without it nothing is added from here; the rows keep their actions.
        fileRefsScope.launch {
            try {
                val taken = ClientsApi.fileRefTakes(props.clientId)
                if (latest.current == token) {
                    takes = taken
                    takesFailed = false
                }
            } catch (_: Throwable) {
                if (latest.current == token) {
                    takes = null
                    takesFailed = true
                }
            }
        }
    }

    val draft = savesAsDraft(props.sandboxOf, props.hasSandbox)
    val expected = expectedMode(draft)

    /** Runs [change] for the ref or form [at]; [done] words what it did, and [added] says the form's work is done. */
    fun run(at: String, action: String, added: Boolean, done: (FileRefChange) -> String, change: suspend () -> FileRefChange) {
        // The client the change is for. By the time it answers the page may be showing another's refs.
        val forClient = props.clientId
        rows.start(at, action)
        note = null
        fileRefsScope.launch {
            try {
                val changed = change()
                if (lastClient.current == forClient) {
                    // Drawn from the answer, before the re-read the bump below starts.
                    refs = withFileRefChange(refs, changed)
                    kept = kept + changed.refId
                    note = done(changed)
                    editing = null
                    if (added) {
                        newId = ""
                        newDescription = ""
                        newPath = ""
                    }
                }
                props.onChanged()
            } catch (e: Throwable) {
                if (lastClient.current == forClient) rows.fail(at, e, props.onChanged)
            } finally {
                rows.finish()
            }
        }
    }

    /** Says [text] beside [at] without sending anything: what the page can tell is wrong by itself. */
    fun refuse(at: String, text: String) {
        rows.refuse(at, text)
        note = null
    }

    val listed = refs
    val taking = takes
    val editable = !props.staticHere

    p {
        className = ClassName("subtitle")
        +fileRefsIntro(props.sandboxOf, props.hasSandbox, props.staticHere)
    }
    loadError?.let { errorText("Couldn't load this client's files.", it) }
    if (listed == null && loadError == null) {
        p {
            className = ClassName("subtitle")
            +"Loading…"
        }
    }
    // One element whether or not there is a note, so that what follows keeps its place -- and with it, where the
    // keyboard is -- when one appears or goes. Empty, it takes no room (`app.css`).
    p {
        className = ClassName("subtitle file-ref-note")
        note?.let { +it }
    }
    if (listed != null && listed.isEmpty()) {
        p {
            className = ClassName("subtitle")
            +fileRefsEmptyText(draft)
        }
    }
    val filter = FileRefFilter(findText, marks)
    val page = listed?.let { fileRefPage(it, filter, showAll, kept) }
    // Finding one among many (issue #1190): only once there are enough to look through.
    if (listed != null && listed.size > 1) {
        div {
            key = "find".unsafeCast<Key>()
            className = ClassName("file-ref-find")
            label {
                className = ClassName("file-ref-field")
                span { +"Find" }
                Input {
                    value = findText
                    placeholder = "Words in an id or what it is for"
                    allowClear = true
                    style = js("({ width: 260 })")
                    onChange = { e -> findText = (e.target.value as? String).orEmpty() }
                }
            }
            fileRefMarkCounts(listed).forEach { (mark, count) ->
                Checkbox {
                    key = mark.unsafeCast<Key>()
                    checked = mark in marks
                    onChange = { e -> marks = if (e.target.checked == true) marks + mark else marks - mark }
                    +"$mark ($count)"
                }
            }
        }
    }
    page?.let { fileRefPageText(it, filter) }?.let { said ->
        p {
            className = ClassName("subtitle")
            +said
        }
    }
    page?.shown?.forEach { ref ->
        div {
            key = ref.refId.unsafeCast<Key>()
            className = ClassName("web-asset")
            div {
                className = ClassName("web-asset-preview")
                val preview = fileRefPreviewArgs(props.clientId, ref)?.let { Http.apiUrl(UADEP.clientFilesDownload, it) }
                if (preview == null) {
                    span {
                        className = ClassName("web-asset-none")
                        +"—"
                    }
                } else {
                    img {
                        src = preview
                        alt = fileRefAltText(ref)
                        // A client may hold two hundred: each is fetched as it nears the screen, not all on opening the page.
                        asDynamic()["loading"] = "lazy"
                    }
                }
            }
            div {
                className = ClassName("web-asset-facts")
                div {
                    className = ClassName("web-asset-name")
                    +ref.refId
                    fileRefTags(ref).forEach { tag ->
                        span {
                            key = tag.unsafeCast<Key>()
                            className = ClassName("file-ref-tag")
                            +tag
                        }
                    }
                }
                if (editing == ref.refId to FACT.describe) {
                    div {
                        className = ClassName("file-ref-describe")
                        Input {
                            value = describeText
                            placeholder = "What it is for; leave it empty to clear"
                            maxLength = FREF.maxDescriptionLength
                            disabled = status.running
                            style = js("({ minWidth: 240 })")
                            onChange = { e -> describeText = e.target.value as String }
                        }
                        Button {
                            type = "primary"
                            loading = status.isBusy(ref.refId, FACT.describe)
                            disabled = status.othersBusy(ref.refId, FACT.describe)
                            onClick = {
                                // What was typed, blank included: blank is how a description is cleared.
                                run(ref.refId, FACT.describe, added = false, ::fileRefDescribedNote) {
                                    ClientsApi.describeFileRef(props.clientId, ref.refId, describeText, expected)
                                }
                            }
                            +"Save"
                        }
                        Button {
                            disabled = status.running
                            onClick = { editing = null }
                            +"Cancel"
                        }
                    }
                } else {
                    ref.description?.let { described -> div { +described } }
                }
                div { +fileRefHoldsText(ref) }
                div {
                    className = ClassName("subtitle")
                    +fileRefWhereText(ref)
                }
                // What to write in copy to show it (issue #1186), and the address that resolves to for its people.
                div {
                    className = ClassName("subtitle file-ref-address")
                    +"In copy: "
                    code { +fileRefImageMarkdown(ref) }
                }
                fileRefAddress(ref, Http::fileRefUrl)?.let { address ->
                    div {
                        className = ClassName("subtitle file-ref-address")
                        +fileRefAddressLead(ref)
                        code { +address }
                        +" "
                        CopyButton {
                            text = address
                            label = "Copy the address of '${ref.refId}'"
                        }
                    }
                }
                fileRefShippedHint(ref)?.let { hint ->
                    div {
                        className = ClassName("subtitle")
                        +hint
                    }
                }
                fileRefRemovedInDraftText(ref)?.let { said ->
                    div {
                        className = ClassName("subtitle")
                        +said
                    }
                }
                if (editable && fileRefRestorable(ref)) {
                    // A draft removed it: there is no entry in the draft to replace or reword, only one to put back.
                    div {
                        className = ClassName("web-asset-actions")
                        Button {
                            loading = status.isBusy(ref.refId, FACT.restore)
                            disabled = status.othersBusy(ref.refId, FACT.restore)
                            onClick = {
                                run(ref.refId, FACT.restore, added = false, ::fileRefRestoredNote) {
                                    ClientsApi.restoreFileRef(props.clientId, ref.refId, expected)
                                }
                            }
                            +"Put back"
                        }
                    }
                } else if (editable) {
                    div {
                        className = ClassName("web-asset-actions")
                        FilePickButton {
                            label = "Replace…"
                            accept = taking?.mimeTypes.orEmpty()
                            primary = false
                            this.busy = status.isBusy(ref.refId, FACT.upload)
                            disabled = status.running
                            onPicked = { file, size ->
                                val tooLarge = fileRefFileProblem(taking, size)
                                if (tooLarge != null) {
                                    refuse(ref.refId, tooLarge)
                                } else {
                                    // No description sent: the ref keeps the one it has.
                                    run(ref.refId, FACT.upload, added = false, ::fileRefUploadedNote) {
                                        ClientsApi.uploadFileRef(props.clientId, ref.refId, file, description = null, expected)
                                    }
                                }
                            }
                        }
                        if (fileRefRepointable(ref, taking)) {
                            Button {
                                disabled = status.running || editing == ref.refId to FACT.local
                                onClick = {
                                    editing = ref.refId to FACT.local
                                    val (root, path) = fileRefRepointStart(ref)
                                    repointRoot = root
                                    repointPath = path
                                }
                                +"Use a kept file…"
                            }
                        }
                        if (fileRefRemovable(ref)) {
                            Button {
                                disabled = status.running || editing == ref.refId to FACT.describe
                                onClick = {
                                    editing = ref.refId to FACT.describe
                                    describeText = ref.description.orEmpty()
                                }
                                +"Reword…"
                            }
                            confirmedRemove(
                                rows, ref.refId, FACT.remove,
                                fileRefRemoveQuestion(ref, props.clientId, draft), fileRefRemoveConsequence(ref, draft),
                            ) {
                                run(ref.refId, FACT.remove, added = false, ::fileRefRemovedNote) {
                                    ClientsApi.removeFileRef(props.clientId, ref.refId, expected)
                                }
                            }
                        }
                    }
                }
                if (editing == ref.refId to FACT.local && taking != null) {
                    div {
                        className = ClassName("file-ref-describe")
                        keptFileFields(fileRefChosenRoot(repointRoot, taking.roots), taking.roots, repointPath, status.running, { repointRoot = it }, { repointPath = it })
                        Button {
                            type = "primary"
                            loading = status.isBusy(ref.refId, FACT.local)
                            disabled = status.othersBusy(ref.refId, FACT.local)
                            onClick = {
                                val root = fileRefChosenRoot(repointRoot, taking.roots)
                                val wrong = fileRefRepointProblem(ref, root, repointPath)
                                when {
                                    wrong != null -> refuse(ref.refId, wrong)
                                    // A root is chosen whenever nothing is wrong; said for the compiler's sake.
                                    root != null ->
                                        // No description sent: the ref keeps the one it has, as a Replace does.
                                        run(ref.refId, FACT.local, added = false, ::fileRefNamedNote) {
                                            ClientsApi.nameLocalFileRef(props.clientId, ref.refId, root, repointPath, description = null, expected)
                                        }
                                }
                            }
                            +"Use this file"
                        }
                        Button {
                            disabled = status.running
                            onClick = { editing = null }
                            +"Cancel"
                        }
                    }
                }
                rowFailure(status, ref.refId, "Couldn't change the file '${ref.refId}'.")
            }
        }
    }
    val atLimit = listed?.let { fileRefAtLimitText(it, taking) }
    if (editable && listed != null && atLimit != null) {
        p {
            key = "at-limit".unsafeCast<Key>()
            className = ClassName("subtitle")
            +atLimit
        }
    }
    if (editable && listed != null && takesFailed) {
        p {
            key = "no-takes".unsafeCast<Key>()
            className = ClassName("subtitle")
            +"What a file may be could not be read just now, so none is added from here. Reload to try again."
        }
    }
    page?.takeIf { it.hidden > 0 }?.let { more ->
        p {
            key = "more".unsafeCast<Key>()
            Button {
                size = "small"
                onClick = { showAll = true }
                +"Show all ${more.matched}"
            }
        }
    }
    // Only once the backend has said what a file may be: the form offers what will be accepted, in its words.
    if (editable && listed != null && taking != null && atLimit == null) {
        div {
            // Keyed, so the form is the same form whatever is drawn above it: a row added by its own upload must
            // not take the keyboard out of it.
            key = "add".unsafeCast<Key>()
            className = ClassName("file-ref-add")
            div {
                className = ClassName("web-asset-name")
                +"Add a file"
            }
            div {
                className = ClassName("file-ref-add-row")
                label {
                    className = ClassName("file-ref-field")
                    span { +"Id" }
                    Input {
                        value = newId
                        placeholder = "Its id, such as heroImage"
                        maxLength = FREF.maxRefIdLength
                        disabled = status.running
                        // antd gives an Input `flex: 1` in a flex row, where a width alone is ignored: an id is
                        // short and says it does not grow, and what it is for takes the room that leaves.
                        style = js("({ width: 220, flex: 'none' })")
                        onChange = { e -> newId = (e.target.value as String).trim() }
                    }
                }
                label {
                    className = ClassName("file-ref-field")
                    span { +"What it is for" }
                    Input {
                        value = newDescription
                        placeholder = "Optional"
                        maxLength = FREF.maxDescriptionLength
                        disabled = status.running
                        style = js("({ minWidth: 240 })")
                        onChange = { e -> newDescription = e.target.value as String }
                    }
                }
            }
            div {
                className = ClassName("file-ref-add-row")
                FilePickButton {
                    label = "Upload…"
                    accept = taking.mimeTypes
                    this.busy = status.isBusy(FACT.adding, FACT.upload)
                    disabled = status.running
                    onPicked = { file, size ->
                        val wrong = fileRefAddUploadProblem(newId, listed, taking, size)
                        if (wrong != null) {
                            refuse(FACT.adding, wrong)
                        } else {
                            val described = fileRefNewDescription(newDescription)
                            run(FACT.adding, FACT.upload, added = true, ::fileRefUploadedNote) {
                                ClientsApi.uploadFileRef(props.clientId, newId, file, described, expected)
                            }
                        }
                    }
                }
                span {
                    className = ClassName("subtitle")
                    +fileRefTakesText(taking)
                    fileRefCountText(listed, taking)?.let { +" $it" }
                }
            }
            // Offered only where the deployment has opened a root to clients, which most have not.
            if (taking.roots.isNotEmpty()) {
                div {
                    className = ClassName("subtitle")
                    +"Or name a file the deployment keeps for this client, by its path in the client's own directory there:"
                }
                div {
                    className = ClassName("file-ref-add-row")
                    keptFileFields(fileRefChosenRoot(newRoot, taking.roots), taking.roots, newPath, status.running, { newRoot = it }, { newPath = it })
                    Button {
                        loading = status.isBusy(FACT.adding, FACT.local)
                        disabled = status.othersBusy(FACT.adding, FACT.local)
                        onClick = {
                            val root = fileRefChosenRoot(newRoot, taking.roots)
                            val wrong = fileRefLocalProblem(newId, listed, root, newPath)
                            when {
                                wrong != null -> refuse(FACT.adding, wrong)
                                // A root is chosen whenever nothing is wrong; said for the compiler's sake.
                                root != null -> {
                                    val described = fileRefNewDescription(newDescription)
                                    run(FACT.adding, FACT.local, added = true, ::fileRefNamedNote) {
                                        ClientsApi.nameLocalFileRef(props.clientId, newId, root, newPath, described, expected)
                                    }
                                }
                            }
                        }
                        +"Use this file"
                    }
                }
            }
            rowFailure(status, FACT.adding, "Couldn't add the file.")
        }
    }
}

/**
 * The two fields that name a file the deployment keeps for the client (issues #1164, #1189): where -- one of the
 * [roots] open to clients -- and its path in the client's own directory there. One drawing for the add form and a
 * row's "Use a kept file…", so the two ask for the same thing in the same words.
 */
private fun ChildrenBuilder.keptFileFields(
    root: String?,
    roots: List<String>,
    path: String,
    disabled: Boolean,
    onRoot: (String?) -> Unit,
    onPath: (String) -> Unit,
) {
    label {
        className = ClassName("file-ref-field")
        span { +"Root" }
        Select {
            value = root
            placeholder = "Where"
            this.disabled = disabled
            style = js("({ minWidth: 160 })")
            options = roots.map { name ->
                val option: dynamic = js("({})")
                option.label = name
                option.value = name
                option
            }.toTypedArray()
            onChange = { chosen -> onRoot(chosen as? String) }
        }
    }
    label {
        className = ClassName("file-ref-field")
        span { +"Path" }
        Input {
            value = path
            placeholder = "Such as marks/hero.png"
            maxLength = FREF.maxPathLength
            this.disabled = disabled
            style = js("({ minWidth: 240 })")
            onChange = { e -> onPath((e.target.value as String).trim()) }
        }
    }
}
